import { Injectable, inject } from '@angular/core';
import { Firestore, collection, doc, getDocs, runTransaction, onSnapshot, Unsubscribe } from '@angular/fire/firestore';

/** The Device ID cannot be used: `taken` = a document already holds it (or a case variant); `race` = it appeared during the save. */
export class DeviceIdTakenError extends Error {
  constructor(public readonly reason: 'taken' | 'race', public readonly deviceId: string) {
    super(`Device ID "${deviceId}" is not available (${reason}).`);
    this.name = 'DeviceIdTakenError';
  }
}

/** Reading the existing devices failed (network, permission). Nothing was written. */
export class DeviceVerifyError extends Error {
  constructor(public readonly original: unknown) {
    super('Could not verify the Device ID.');
    this.name = 'DeviceVerifyError';
  }
}

/** Device access for Konobox Management, kept behind a service so the component can be tested with a fake. */
@Injectable({ providedIn: 'root' })
export class KonoboxDeviceService {
  private firestore = inject(Firestore);

  /** All raw `devices` documents (id + data). */
  async listDeviceDocs(): Promise<{ id: string; data: any }[]> {
    const snap = await getDocs(collection(this.firestore, 'devices'));
    return snap.docs.map(d => ({ id: d.id, data: d.data() }));
  }

  /**
   * Real-time listener for the `devices` collection (KAN-164 / Ticket 2).
   * Emits the full set of device documents upon collection changes.
   * Returns an Unsubscribe function invoked on component destruction.
   */
  watchDevices(next: (docs: { id: string; data: any }[]) => void, error?: (err: any) => void): Unsubscribe {
    const colRef = collection(this.firestore, 'devices');
    return onSnapshot(
      colRef,
      snapshot => {
        const docs = snapshot.docs.map(d => ({ id: d.id, data: d.data() }));
        next(docs);
      },
      err => {
        if (error) {
          error(err);
        } else {
          console.error('Error watching devices collection:', err);
        }
      }
    );
  }

  /**
   * Creates `devices/{cleanId}` only if no usable document exists (KAN-163 Part A).
   * - The check reads Firestore (never the grid) and also catches case variants such as `kb-la-01` for `KB-LA-01`.
   * - An existing active document, or a soft-deleted document of another brand, is rejected and left unchanged.
   * - A soft-deleted document of the Konobox brand is replaced in full (set without merge), so stale telemetry
   *   fields disappear; subcollections (sessions, reports, commands) are not touched.
   * - The write is a transaction that re-reads the document, so a competing create makes exactly one of them fail.
   */
  async createDeviceIfAbsent(cleanId: string, payload: Record<string, any>, konoboxBrandId: string): Promise<'created' | 'replaced'> {
    let docs: { id: string; data: any }[];
    try {
      docs = await this.listDeviceDocs();
    } catch (err) {
      throw new DeviceVerifyError(err);
    }

    const wanted = cleanId.toLowerCase();
    const sameKey = docs.filter(d => d.id.toLowerCase() === wanted || String(d.data?.['deviceId'] || '').toLowerCase() === wanted);
    if (sameKey.some(d => d.id !== cleanId)) {
      throw new DeviceIdTakenError('taken', cleanId);
    }
    const existing = sameKey.find(d => d.id === cleanId);
    if (existing && !DeviceReplaceRule.canReplace(existing.data, konoboxBrandId)) {
      throw new DeviceIdTakenError('taken', cleanId);
    }

    const ref = doc(this.firestore, 'devices', cleanId);
    return runTransaction(this.firestore, async tx => {
      const snap = await tx.get(ref);
      if (snap.exists()) {
        // Only the soft-deleted Konobox document we saw above may be replaced; anything else appeared meanwhile.
        if (!existing || !DeviceReplaceRule.canReplace(snap.data(), konoboxBrandId)) {
          throw new DeviceIdTakenError(existing ? 'taken' : 'race', cleanId);
        }
        tx.set(ref, payload);
        return 'replaced' as const;
      }
      if (existing) {
        throw new DeviceIdTakenError('race', cleanId);
      }
      tx.set(ref, payload);
      return 'created' as const;
    });
  }
}

export class DeviceReplaceRule {
  /** Soft-deleted AND owned by the Konobox brand. A missing `isDeleted` counts as active. */
  static canReplace(data: any, konoboxBrandId: string): boolean {
    return data?.['isDeleted'] === true && !!konoboxBrandId && data?.['brandId'] === konoboxBrandId;
  }
}
